/**
 * CasesReports — Dashboard mit Top-KPIs, 7 Tabs, Drilldown-Sources.
 *
 * @page /cases/reports
 * @roles admin, manager
 * @api GET /api/cases/reports/overview · operational · sla · team · kunden · volumen · zufriedenheit
 * @i18n casesReports
 * @since v2.34.0  (Refactor #1061 v2.472.0 — Dashboard-Stil + neue Tabs)
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import useTabParam from '../hooks/useTabParam';
import {
  Shield, Users, Building, TrendingUp, Star, BarChart3,
  LayoutDashboard, Activity, UserCog, Heart, Crown, Calendar,
  Download, FileText, FileSpreadsheet, FileType, Plus, Trash2, Play, Pause as PauseIcon,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { KpiCard } from '../components/ui/KpiCard';
import DateRangeFilter from '../components/DateRangeFilter';
import { getDateRange } from '../utils/dateUtils';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const API = import.meta.env.VITE_API_URL || '';

const COLORS = {
  violet: '#338edc', cyan: '#22D3EE', indigo: '#6366F1', amber: '#F59E0B',
  emerald: '#10B981', red: '#EF4444', gray: '#E5E7EB', grayText: '#9CA3AF',
};
const PIE_COLORS = [COLORS.violet, COLORS.cyan, COLORS.indigo, COLORS.amber, COLORS.emerald, COLORS.grayText];

function fmt(n) { return n == null ? '–' : Number(n).toLocaleString('de-DE'); }
function fmtD1(n) { return n == null ? '–' : Number(n).toFixed(1).replace('.', ','); }
function fmtPct(n) { return n == null ? '–' : fmtD1(n) + '%'; }
function fmtHours(h) { return h == null ? '–' : `${fmtD1(h)} h`; }

function monatLabel(m) {
  if (!m) return '';
  const [y, mm] = m.split('-');
  return `${mm}/${y.slice(2)}`;
}
function dayLabel(d) {
  if (!d) return '';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, '0')}.${String(dt.getMonth() + 1).padStart(2, '0')}`;
}

const STAGE_COLOR = {
  waiting_for_support: COLORS.amber,
  in_progress: COLORS.violet,
  clarification_with_recipient: COLORS.cyan,
  solution_proposed: COLORS.indigo,
  reopened: COLORS.red,
  response_required: COLORS.amber,
};

const TAB_DEFS = [
  { key: 'overview',     labelKey: 'casesReports.tabOverview',       Icon: LayoutDashboard, phase: 1 },
  { key: 'operational',  labelKey: 'casesReports.tabOperational',    Icon: Activity,        phase: 1 },
  { key: 'sla',          labelKey: 'casesReports.tabSla',            Icon: Shield,          phase: 1 },
  { key: 'agent',        labelKey: 'casesReports.tabAgent',          Icon: UserCog,         phase: 1 },
  { key: 'customer',     labelKey: 'casesReports.tabCustomer',       Icon: Heart,           phase: 1 },
  { key: 'ceo',          labelKey: 'casesReports.tabCeo',            Icon: Crown,           phase: 1 },
  { key: 'schedule',     labelKey: 'casesReports.tabSchedule',       Icon: Calendar,        phase: 1 },
];

// ─── Empty State ──────────────────────────────────────────────
function EmptyState({ t }) {
  return (
    <div className="bg-surface-card border border-line rounded-card">
      <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
        <div className="w-12 h-12 rounded-card bg-surface-inset border border-line flex items-center justify-center mb-4">
          <BarChart3 className="w-6 h-6 text-content-tertiary" />
        </div>
        <h2 className="text-base font-bold text-content mb-2">{t('casesReports.noData')}</h2>
        <p className="text-sm text-[var(--text-secondary)] max-w-md">{t('casesReports.noDataDesc')}</p>
      </div>
    </div>
  );
}

// ─── Section Card ─────────────────────────────────────────────
function Section({ title, subtitle, action, children, className = '' }) {
  return (
    <div className={`bg-surface-card border border-line rounded-card p-5 ${className}`}>
      {(title || action) && (
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            {title && <h3 className="text-sm font-bold text-content">{title}</h3>}
            {subtitle && <p className="text-xs text-[var(--text-tertiary)] mt-0.5">{subtitle}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

// ─── Chart Tooltip ────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface-elevated border border-line rounded-lg shadow-sm p-3 text-xs">
      <p className="font-semibold text-content mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="flex justify-between gap-4">
          <span>{p.name}</span>
          <span className="font-semibold">{typeof p.value === 'number' && p.value % 1 !== 0 ? fmtD1(p.value) : fmt(p.value)}</span>
        </p>
      ))}
    </div>
  );
}

// ─── Sparkline-Mini (60×24) ────────────────────────────────────
function Sparkline({ data, color, height = 36 }) {
  if (!data?.length) return null;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data}>
        <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} dot={false} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ─── Top-KPI-Row (immer sichtbar, ueber Tab-Bar) ───────────────
function TopKpis({ data, t }) {
  if (!data) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-5">
        {[0,1,2,3,4].map(i => (
          <div key={i} className="bg-surface-card border border-line rounded-card p-4 h-[112px] animate-pulse">
            <div className="h-3 w-20 bg-surface-inset rounded" />
            <div className="h-7 w-16 bg-surface-inset rounded mt-2" />
            <div className="h-9 w-full bg-surface-inset rounded mt-2" />
          </div>
        ))}
      </div>
    );
  }
  const k = data.kpis || {};
  const sp = data.sparklines || {};
  const cards = [
    { label: t('casesReports.kpiOpen'), value: fmt(k.open_cases), spark: null, color: COLORS.amber, sub: null },
    { label: t('casesReports.kpiTotalRange'), value: fmt(k.total_cases), spark: sp.cases_new_14d, color: COLORS.violet, sub: t('casesReports.last14d') },
    { label: t('casesReports.kpiAvgResolution'), value: fmtHours(k.avg_resolution_hours), spark: null, color: COLORS.cyan, sub: null },
    { label: t('casesReports.kpiSlaCompliance'), value: k.sla_compliance_pct == null ? '–' : fmtPct(k.sla_compliance_pct), spark: sp.sla_breach_14d, color: k.sla_compliance_pct >= 90 ? COLORS.emerald : (k.sla_compliance_pct >= 70 ? COLORS.amber : COLORS.red), sub: `${fmt(k.sla_breached)} ${t('casesReports.breaches')}` },
    { label: t('casesReports.kpiCsat'), value: k.csat_avg == null ? '–' : `${fmtD1(k.csat_avg)} / 5`, spark: null, color: COLORS.indigo, sub: `${fmt(k.csat_responses)} ${t('casesReports.responses')}` },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-5">
      {cards.map((c, i) => (
        <div key={i} className="bg-surface-card border border-line rounded-card p-4">
          <p className="text-[11px] font-bold text-[var(--text-tertiary)] uppercase tracking-wider">{c.label}</p>
          <p className="text-2xl font-bold text-content mt-1.5" style={{ color: c.color }}>{c.value}</p>
          {c.sub && <p className="text-[11px] text-[var(--text-tertiary)] mt-0.5">{c.sub}</p>}
          {c.spark && <div className="mt-1.5"><Sparkline data={c.spark} color={c.color} /></div>}
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: ÜBERSICHT (#1061 Phase 1 — neu)
// ═══════════════════════════════════════════════════════════════
function TabUebersicht({ overviewData, t }) {
  if (!overviewData) return <EmptyState t={t} />;
  const sp = overviewData.sparklines || {};
  const newD = sp.cases_new_14d || [];
  const closedD = sp.cases_closed_14d || [];
  // Combine for dual-area chart
  const merged = newD.map((row, i) => ({
    label: dayLabel(row.d),
    neu: row.v,
    erledigt: closedD[i]?.v ?? 0,
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title={t('casesReports.last14dTrend')} subtitle={t('casesReports.last14dTrendSub')}>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={merged}>
              <defs>
                <linearGradient id="grNeu" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={COLORS.violet} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={COLORS.violet} stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="grClosed" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={COLORS.emerald} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={COLORS.emerald} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card, #E5E7EB)" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: COLORS.grayText }} />
              <YAxis tick={{ fontSize: 11, fill: COLORS.grayText }} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="neu" name={t('casesReports.new')} stroke={COLORS.violet} strokeWidth={2} fill="url(#grNeu)" />
              <Area type="monotone" dataKey="erledigt" name={t('casesReports.resolved')} stroke={COLORS.emerald} strokeWidth={2} fill="url(#grClosed)" />
            </AreaChart>
          </ResponsiveContainer>
        </Section>

        <Section title={t('casesReports.last14dBreaches')} subtitle={t('casesReports.last14dBreachesSub')}>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={(sp.sla_breach_14d || []).map(r => ({ label: dayLabel(r.d), v: r.v }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card, #E5E7EB)" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: COLORS.grayText }} />
              <YAxis tick={{ fontSize: 11, fill: COLORS.grayText }} allowDecimals={false} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="v" name={t('casesReports.breaches')} fill={COLORS.red} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Section>
      </div>

      <p className="text-[11px] text-[var(--text-tertiary)]">
        {t('casesReports.cachedHint')}: {overviewData.cached_until ? new Date(overviewData.cached_until).toLocaleTimeString('de-DE') : '—'}
      </p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: OPERATIONAL (#1061 Phase 1 — neu)
// ═══════════════════════════════════════════════════════════════
function TabOperational({ data, t }) {
  if (!data) return <EmptyState t={t} />;
  const { volumen_trend = [], stage_verteilung = [], source_mix = [], backlog = {} } = data;
  const totalSources = source_mix.reduce((s, r) => s + r.count, 0);

  return (
    <div className="space-y-6">
      {/* Backlog-Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <KpiCard label={t('casesReports.backlogOpen')} value={fmt(backlog.total_open)} icon={Activity} color="bg-primary-500/10 text-primary-600" />
        <KpiCard label={t('casesReports.backlog14d')} value={fmt(backlog.aged_14d)} icon={Activity} color="bg-amber-500/10 text-amber-600" />
        <KpiCard label={t('casesReports.backlog30d')} value={fmt(backlog.aged_30d)} icon={Activity} color="bg-red-500/10 text-red-500" />
      </div>

      {/* Volumen-Trend */}
      {volumen_trend.length > 0 && (
        <Section title={t('casesReports.weeklyVolume')} subtitle={t('casesReports.weeklyVolumeSub')}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={volumen_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card, #E5E7EB)" />
              <XAxis dataKey="kw" tick={{ fontSize: 11, fill: COLORS.grayText }} />
              <YAxis tick={{ fontSize: 11, fill: COLORS.grayText }} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="neu" name={t('casesReports.new')} fill={COLORS.violet} radius={[4, 4, 0, 0]} />
              <Bar dataKey="erledigt" name={t('casesReports.resolved')} fill={COLORS.emerald} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Stage-Verteilung Donut */}
        {stage_verteilung.length > 0 && (
          <Section title={t('casesReports.openByStage')} subtitle={t('casesReports.openByStageSub')}>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={stage_verteilung}
                  dataKey="count"
                  nameKey="stage"
                  cx="50%" cy="50%"
                  innerRadius={55} outerRadius={90} paddingAngle={3}
                  label={({ stage, count }) => `${t('cases.stage.' + stage) || stage} (${count})`}
                >
                  {stage_verteilung.map((row, i) => (
                    <Cell key={i} fill={STAGE_COLOR[row.stage] || PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </Section>
        )}

        {/* Source-Mix Bar */}
        {source_mix.length > 0 && (
          <Section title={t('casesReports.sourceMix')} subtitle={t('casesReports.sourceMixSub')}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={source_mix.map(r => ({ ...r, pct: totalSources > 0 ? Math.round((r.count / totalSources) * 1000) / 10 : 0 }))} layout="vertical" margin={{ left: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card, #E5E7EB)" />
                <XAxis type="number" tick={{ fontSize: 11, fill: COLORS.grayText }} />
                <YAxis dataKey="source" type="category" tick={{ fontSize: 11, fill: COLORS.grayText }} width={75} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" name={t('casesReports.cases')} fill={COLORS.violet} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Section>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: SLA (REUSE existing /sla endpoint, refactored layout)
// ═══════════════════════════════════════════════════════════════
function TabSLA({ data, t }) {
  if (!data || data.gesamt.total_cases === 0) return <EmptyState t={t} />;
  const { gesamt, pro_klasse, trend, top_verletzungen } = data;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label={t('casesReports.totalCases')} value={fmt(gesamt.total_cases)} icon={Shield} color="bg-primary-500/10 text-primary-600" />
        <KpiCard label={t('casesReports.slaCompliance')} value={fmtPct(gesamt.einhaltungsquote)} icon={Shield} color="bg-emerald-500/10 text-emerald-600" />
        <KpiCard label={t('casesReports.slaViolations')} value={fmt(gesamt.sla_verletzt)} icon={Shield} color="bg-red-500/10 text-red-500" />
        <KpiCard label={t('casesReports.avgResponseTime')} value={`${fmt(gesamt.durchschnittliche_antwortzeit_min)} ${t('casesReports.min')}`} icon={Shield} color="bg-cyan-500/10 text-cyan-600" />
      </div>

      {pro_klasse.length > 0 && (
        <Section title={t('casesReports.complianceByClass')}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line">
                  <th className="text-left py-2 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">{t('casesReports.class')}</th>
                  <th className="text-right py-2 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">{t('casesReports.total')}</th>
                  <th className="text-right py-2 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">{t('casesReports.complied')}</th>
                  <th className="text-right py-2 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">{t('casesReports.violated')}</th>
                  <th className="text-right py-2 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">{t('casesReports.quote')}</th>
                  <th className="text-right py-2 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">{t('casesReports.avgMin')}</th>
                </tr>
              </thead>
              <tbody>
                {pro_klasse.map((k, i) => (
                  <tr key={i} className="border-b border-line/50">
                    <td className="py-2.5 font-medium text-content">{k.klasse}</td>
                    <td className="py-2.5 text-right text-content">{fmt(k.total)}</td>
                    <td className="py-2.5 text-right text-content">{fmt(k.eingehalten)}</td>
                    <td className="py-2.5 text-right text-content">{fmt(k.verletzt)}</td>
                    <td className="py-2.5 text-right font-semibold text-content">{fmtPct(k.quote)}</td>
                    <td className="py-2.5 text-right text-content">{fmt(k.avg_min)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {trend.length > 0 && (
          <Section title={t('casesReports.monthlyTrend')}>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={trend.map(r => ({ ...r, label: monatLabel(r.monat) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card, #E5E7EB)" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: COLORS.grayText }} />
                <YAxis tick={{ fontSize: 11, fill: COLORS.grayText }} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="quote" name={t('casesReports.complianceRate')} stroke={COLORS.violet} strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="verletzt" name={t('casesReports.violations')} stroke={COLORS.red} strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </Section>
        )}

        {top_verletzungen.length > 0 && (
          <Section title={t('casesReports.topViolations')}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line">
                    <th className="text-left py-2 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">{t('casesReports.customer')}</th>
                    <th className="text-left py-2 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">{t('casesReports.class')}</th>
                    <th className="text-right py-2 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">{t('casesReports.count')}</th>
                  </tr>
                </thead>
                <tbody>
                  {top_verletzungen.map((v, i) => (
                    <tr key={i} className="border-b border-line/50">
                      <td className="py-2 text-content">{v.kunde}</td>
                      <td className="py-2 text-content-secondary">{v.klasse}</td>
                      <td className="py-2 text-right font-semibold text-content">{fmt(v.count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// HEATMAP-CELL — Tailwind opacity scale based on avg-minutes
// ═══════════════════════════════════════════════════════════════
function heatmapClass(avgMin) {
  if (avgMin == null) return 'bg-surface-inset border-line';
  if (avgMin < 15) return 'bg-emerald-500/85 border-emerald-600/30 text-white';
  if (avgMin < 30) return 'bg-emerald-500/65 border-emerald-600/30 text-white';
  if (avgMin < 60) return 'bg-emerald-500/45 border-emerald-600/30';
  if (avgMin < 120) return 'bg-amber-500/55 border-amber-600/30';
  if (avgMin < 240) return 'bg-amber-500/80 border-amber-600/30 text-white';
  if (avgMin < 480) return 'bg-red-500/55 border-red-600/30 text-white';
  return 'bg-red-500/85 border-red-600/30 text-white';
}

const DOW_LABELS_DE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

// ═══════════════════════════════════════════════════════════════
// TAB: AGENT (#1061 Phase 2)
// ═══════════════════════════════════════════════════════════════
function TabAgent({ data, t }) {
  if (!data) return <EmptyState t={t} />;
  const { reply_heatmap = [], agents = [], top_agents = [], bottom_agents = [], capacity = [], workload_curve = [], workload_agents = [] } = data;

  // Reorder DoW: Mo Di Mi Do Fr Sa So (ISO-style)
  const ROW_ORDER = [1, 2, 3, 4, 5, 6, 0];
  const filledCount = reply_heatmap.filter(c => c.avg_min != null).length;

  const topCombined = top_agents.map(a => ({ name: a.name, value: a.sla_compliance_pct, label: 'top' }));
  const bottomCombined = bottom_agents.map(a => ({ name: a.name, value: a.sla_compliance_pct, label: 'bottom' }));

  return (
    <div className="space-y-6">
      {/* Reply-Heatmap */}
      <Section title={t('casesReports.replyHeatmapTitle')} subtitle={t('casesReports.replyHeatmapSub')}>
        {filledCount === 0 ? (
          <p className="text-sm text-[var(--text-tertiary)] py-6 text-center">{t('casesReports.heatmapEmpty')}</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full">
                {/* Header: hours 0-23 */}
                <div className="flex gap-px ml-8 mb-1">
                  {Array.from({ length: 24 }).map((_, h) => (
                    <div key={h} className="w-7 text-center text-[9px] font-semibold text-[var(--text-tertiary)] tabular-nums">
                      {h}
                    </div>
                  ))}
                </div>
                {/* Grid */}
                {ROW_ORDER.map(d => (
                  <div key={d} className="flex gap-px items-center mb-px">
                    <div className="w-8 text-[10px] font-bold text-[var(--text-secondary)] uppercase">{DOW_LABELS_DE[d]}</div>
                    {Array.from({ length: 24 }).map((_, h) => {
                      const cell = reply_heatmap.find(c => c.dow === d && c.hour === h);
                      const avg = cell?.avg_min;
                      const cnt = cell?.cases || 0;
                      const cls = heatmapClass(avg);
                      const tooltip = avg == null
                        ? `${DOW_LABELS_DE[d]} ${h}:00 — ${t('casesReports.noData')}`
                        : `${DOW_LABELS_DE[d]} ${h}:00 — Ø ${avg} min · ${cnt} ${t('casesReports.cases')}`;
                      return (
                        <div
                          key={h}
                          title={tooltip}
                          className={`w-7 h-7 rounded border ${cls} flex items-center justify-center text-[10px] font-bold tabular-nums transition-opacity hover:opacity-80`}
                        >
                          {avg != null && cnt > 0 ? avg : ''}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
            {/* Legende */}
            <div className="flex items-center gap-3 mt-4 flex-wrap">
              <span className="text-[10px] font-semibold text-[var(--text-tertiary)] uppercase">{t('casesReports.replyTime')}:</span>
              {[
                { lbl: '< 15 min', cls: 'bg-emerald-500/85' },
                { lbl: '15–30 min', cls: 'bg-emerald-500/65' },
                { lbl: '30–60 min', cls: 'bg-emerald-500/45' },
                { lbl: '1–2 h', cls: 'bg-amber-500/55' },
                { lbl: '2–4 h', cls: 'bg-amber-500/80' },
                { lbl: '4–8 h', cls: 'bg-red-500/55' },
                { lbl: '> 8 h', cls: 'bg-red-500/85' },
              ].map((l, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <div className={`w-3 h-3 rounded ${l.cls} border border-line`} />
                  <span className="text-[10px] text-[var(--text-secondary)]">{l.lbl}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top-Agents */}
        {top_agents.length > 0 && (
          <Section title={t('casesReports.topAgents')} subtitle={t('casesReports.topAgentsSub')}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={topCombined} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card, #E5E7EB)" />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11, fill: COLORS.grayText }} unit="%" />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: COLORS.grayText }} width={90} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="value" name={t('casesReports.slaCompliance')} fill={COLORS.emerald} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Section>
        )}

        {/* Bottom-Agents */}
        {bottom_agents.length > 0 && (
          <Section title={t('casesReports.bottomAgents')} subtitle={t('casesReports.bottomAgentsSub')}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={bottomCombined} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card, #E5E7EB)" />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11, fill: COLORS.grayText }} unit="%" />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: COLORS.grayText }} width={90} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="value" name={t('casesReports.slaCompliance')} fill={COLORS.red} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Section>
        )}
      </div>

      {/* Capacity */}
      {capacity.length > 0 && (
        <Section title={t('casesReports.capacityTitle')} subtitle={t('casesReports.capacitySub')}>
          <div className="space-y-2.5">
            {capacity.map((c, i) => {
              const max = Math.max(...capacity.map(x => x.open_cases), 1);
              const pct = (c.open_cases / max) * 100;
              const color = pct > 75 ? COLORS.red : pct > 50 ? COLORS.amber : COLORS.violet;
              return (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-32 text-xs font-medium text-content truncate">{c.name}</div>
                  <div className="flex-1 h-6 bg-surface-inset rounded-md relative overflow-hidden">
                    <div className="h-full transition-all rounded-md" style={{ width: `${pct}%`, background: color }} />
                  </div>
                  <div className="w-12 text-xs font-bold tabular-nums text-right text-content">{c.open_cases}</div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* Workload-Curve */}
      {workload_curve.length > 0 && (
        <Section title={t('casesReports.workloadCurve')} subtitle={t('casesReports.workloadCurveSub')}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={workload_curve}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card, #E5E7EB)" />
              <XAxis dataKey="kw" tick={{ fontSize: 11, fill: COLORS.grayText }} />
              <YAxis tick={{ fontSize: 11, fill: COLORS.grayText }} allowDecimals={false} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {workload_agents.slice(0, 8).map((agent, i) => (
                <Line
                  key={agent}
                  type="monotone"
                  dataKey={agent}
                  stroke={PIE_COLORS[i % PIE_COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Section>
      )}

      {/* Per-Agent-Tabelle */}
      {agents.length > 0 && (
        <Section title={t('casesReports.agentTableTitle')}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line">
                  <th className="text-left py-2 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">{t('casesReports.employee')}</th>
                  <th className="text-right py-2 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">{t('casesReports.totalCases')}</th>
                  <th className="text-right py-2 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">{t('casesReports.closedCases')}</th>
                  <th className="text-right py-2 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">{t('casesReports.avgReplyMin')}</th>
                  <th className="text-right py-2 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">{t('casesReports.avgResolutionH')}</th>
                  <th className="text-right py-2 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">{t('casesReports.slaCompliance')}</th>
                </tr>
              </thead>
              <tbody>
                {agents.map((a, i) => (
                  <tr key={i} className="border-b border-line/50">
                    <td className="py-2.5 font-medium text-content">{a.name}</td>
                    <td className="py-2.5 text-right text-content tabular-nums">{fmt(a.total_cases)}</td>
                    <td className="py-2.5 text-right text-content tabular-nums">{fmt(a.closed_cases)}</td>
                    <td className="py-2.5 text-right text-content tabular-nums">{a.avg_reply_min != null ? `${fmt(a.avg_reply_min)} min` : '–'}</td>
                    <td className="py-2.5 text-right text-content tabular-nums">{a.avg_resolution_h != null ? `${fmtD1(a.avg_resolution_h)} h` : '–'}</td>
                    <td className="py-2.5 text-right font-semibold tabular-nums" style={{
                      color: a.sla_compliance_pct == null ? 'var(--text-tertiary)' :
                        a.sla_compliance_pct >= 90 ? COLORS.emerald :
                        a.sla_compliance_pct >= 70 ? COLORS.amber : COLORS.red,
                    }}>
                      {a.sla_compliance_pct == null ? '–' : fmtPct(a.sla_compliance_pct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CUSTOMER-DRILLDOWN-MODAL
// ═══════════════════════════════════════════════════════════════
function CustomerDrilldownModal({ kundeId, von, bis, includeTest, onClose, t }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const testQs = includeTest ? '&include_test=1' : '';
    fetch(`${API}/api/cases/reports/customer-health?kunde_id=${kundeId}&von=${von}&bis=${bis}${testQs}`, { credentials: 'include' })
      .then(r => r.json())
      .then(j => { if (!cancel) { setData(j.drilldown); setLoading(false); } })
      .catch(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [kundeId, von, bis, includeTest]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-surface-card border border-line rounded-card w-full max-w-3xl max-h-[88vh] overflow-y-auto shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-surface-card border-b border-line px-5 py-3 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-content">{data?.kunde?.name || t('casesReports.customer')}</h3>
            {data?.kunde?.segment && <p className="text-xs text-[var(--text-tertiary)]">{t('casesReports.segment')}: {data.kunde.segment}</p>}
          </div>
          <button onClick={onClose} className="px-3 py-1 text-xs font-semibold rounded-md hover:bg-surface-inset text-[var(--text-secondary)]">✕</button>
        </div>
        <div className="p-5 space-y-4">
          {loading && <div className="flex justify-center py-8"><div className="animate-spin h-6 w-6 border-2 border-primary-500 border-t-transparent rounded-full" /></div>}
          {!loading && !data && <p className="text-sm text-[var(--text-tertiary)] py-6 text-center">{t('casesReports.noData')}</p>}
          {!loading && data && (
            <>
              {data.monthly?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-2">{t('casesReports.monthlyTrend')}</p>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={data.monthly.map(m => ({ ...m, label: monatLabel(m.monat) }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card, #E5E7EB)" />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: COLORS.grayText }} />
                      <YAxis tick={{ fontSize: 10, fill: COLORS.grayText }} allowDecimals={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Bar dataKey="total" name={t('casesReports.total')} fill={COLORS.violet} radius={[3, 3, 0, 0]} />
                      <Bar dataKey="closed" name={t('casesReports.closedCases')} fill={COLORS.emerald} radius={[3, 3, 0, 0]} />
                      <Bar dataKey="sla_breached" name={t('casesReports.slaViolations')} fill={COLORS.red} radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div>
                <p className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-2">
                  {t('casesReports.recentCases')} ({data.cases?.length || 0})
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-line">
                        <th className="text-left py-1.5 font-semibold text-content-tertiary">ID</th>
                        <th className="text-left py-1.5 font-semibold text-content-tertiary">{t('casesReports.title')}</th>
                        <th className="text-left py-1.5 font-semibold text-content-tertiary">{t('cases.stage') || 'Stage'}</th>
                        <th className="text-right py-1.5 font-semibold text-content-tertiary">{t('casesReports.replyTime')}</th>
                        <th className="text-right py-1.5 font-semibold text-content-tertiary">{t('casesReports.avgResolutionTime')}</th>
                        <th className="text-left py-1.5 font-semibold text-content-tertiary">{t('casesReports.employee')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.cases?.map((c) => (
                        <tr key={c.id} className="border-b border-line/40">
                          <td className="py-1.5 text-content tabular-nums">#{c.id}</td>
                          <td className="py-1.5 text-content max-w-xs truncate">{c.title}</td>
                          <td className="py-1.5 text-content-secondary">{c.stage}</td>
                          <td className="py-1.5 text-right tabular-nums text-content">{c.first_reply_min != null ? `${c.first_reply_min}m` : '–'}</td>
                          <td className="py-1.5 text-right tabular-nums text-content">{c.resolution_h != null ? `${fmtD1(c.resolution_h)}h` : '–'}</td>
                          <td className="py-1.5 text-content-secondary truncate">{c.assigned_name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: CUSTOMER-HEALTH (#1061 Phase 2)
// ═══════════════════════════════════════════════════════════════
function TabCustomer({ data, von, bis, includeTest, t }) {
  const [drillKundeId, setDrillKundeId] = useState(null);
  if (!data) return <EmptyState t={t} />;
  const { top_kunden = [], repeat_distribution = [], time_per_case_hist = [] } = data;

  const totalRepeat = repeat_distribution.reduce((s, r) => s + r.kunden_count, 0);
  const totalHist = time_per_case_hist.reduce((s, r) => s + r.count, 0);

  return (
    <>
      <div className="space-y-6">
        {/* Top-15-Kunden BarChart */}
        {top_kunden.length > 0 && (
          <Section
            title={t('casesReports.top15Kunden')}
            subtitle={t('casesReports.top15KundenSub')}
            action={<span className="text-[10px] text-[var(--text-tertiary)]">{t('casesReports.clickForDetail')}</span>}
          >
            <ResponsiveContainer width="100%" height={Math.max(top_kunden.length * 28, 200)}>
              <BarChart data={top_kunden} layout="vertical" margin={{ left: 130 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card, #E5E7EB)" />
                <XAxis type="number" tick={{ fontSize: 11, fill: COLORS.grayText }} allowDecimals={false} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: COLORS.grayText }} width={140} />
                <Tooltip content={<ChartTooltip />} />
                <Bar
                  dataKey="total_cases"
                  name={t('casesReports.cases')}
                  fill={COLORS.violet}
                  radius={[0, 4, 4, 0]}
                  onClick={(d) => setDrillKundeId(d.kunde_id)}
                  style={{ cursor: 'pointer' }}
                />
              </BarChart>
            </ResponsiveContainer>
          </Section>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Repeat-Verteilung */}
          {repeat_distribution.length > 0 && (
            <Section title={t('casesReports.repeatCases')} subtitle={t('casesReports.repeatCasesSub')}>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={repeat_distribution}
                    dataKey="kunden_count"
                    nameKey="bucket"
                    cx="50%" cy="50%"
                    innerRadius={50} outerRadius={90} paddingAngle={3}
                    label={({ bucket, kunden_count }) => `${bucket} (${kunden_count})`}
                  >
                    {repeat_distribution.map((row, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <p className="text-[11px] text-center text-[var(--text-tertiary)] mt-2">
                {t('casesReports.totalCustomers')}: {fmt(totalRepeat)}
              </p>
            </Section>
          )}

          {/* Zeit-pro-Case Histogramm */}
          {totalHist > 0 ? (
            <Section title={t('casesReports.timePerCaseHist')} subtitle={t('casesReports.timePerCaseHistSub')}>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={time_per_case_hist}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card, #E5E7EB)" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: COLORS.grayText }} />
                  <YAxis tick={{ fontSize: 11, fill: COLORS.grayText }} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="count" name={t('casesReports.cases')} fill={COLORS.cyan} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Section>
          ) : (
            <Section title={t('casesReports.timePerCaseHist')} subtitle={t('casesReports.timePerCaseHistSub')}>
              <p className="text-sm text-[var(--text-tertiary)] py-8 text-center">{t('casesReports.histEmpty')}</p>
            </Section>
          )}
        </div>

        {/* Tabelle */}
        {top_kunden.length > 0 && (
          <Section title={t('casesReports.customerTableTitle')}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line">
                    <th className="text-left py-2 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">{t('casesReports.customer')}</th>
                    <th className="text-left py-2 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">{t('casesReports.segment')}</th>
                    <th className="text-right py-2 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">{t('casesReports.totalCases')}</th>
                    <th className="text-right py-2 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">{t('casesReports.openCases')}</th>
                    <th className="text-right py-2 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">{t('casesReports.avgReplyMin')}</th>
                    <th className="text-right py-2 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">{t('casesReports.slaCompliance')}</th>
                    <th className="text-right py-2 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary"> </th>
                  </tr>
                </thead>
                <tbody>
                  {top_kunden.map((k) => (
                    <tr key={k.kunde_id} className="border-b border-line/50 hover:bg-surface-inset/50 cursor-pointer" onClick={() => setDrillKundeId(k.kunde_id)}>
                      <td className="py-2.5 font-medium text-content">{k.name}</td>
                      <td className="py-2.5 text-content-secondary">{k.segment}</td>
                      <td className="py-2.5 text-right tabular-nums text-content">{fmt(k.total_cases)}</td>
                      <td className="py-2.5 text-right tabular-nums text-content">{fmt(k.open_cases)}</td>
                      <td className="py-2.5 text-right tabular-nums text-content">{k.avg_reply_min != null ? `${fmt(k.avg_reply_min)} min` : '–'}</td>
                      <td className="py-2.5 text-right font-semibold tabular-nums" style={{
                        color: k.sla_compliance_pct == null ? 'var(--text-tertiary)' :
                          k.sla_compliance_pct >= 90 ? COLORS.emerald :
                          k.sla_compliance_pct >= 70 ? COLORS.amber : COLORS.red,
                      }}>
                        {k.sla_compliance_pct == null ? '–' : fmtPct(k.sla_compliance_pct)}
                      </td>
                      <td className="py-2.5 text-right text-[10px] text-primary-600">{t('casesReports.detail')} →</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}
      </div>

      {drillKundeId && (
        <CustomerDrilldownModal
          kundeId={drillKundeId}
          von={von}
          bis={bis}
          includeTest={includeTest}
          onClose={() => setDrillKundeId(null)}
          t={t}
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: Coming-Soon Placeholder (Phase 2/3/4)
// ═══════════════════════════════════════════════════════════════
function TabComingSoon({ phase, t }) {
  return (
    <div className="bg-surface-card border border-line rounded-card">
      <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
        <div className="w-12 h-12 rounded-card bg-primary-500/10 border border-primary-500/30 flex items-center justify-center mb-4">
          <Calendar className="w-6 h-6 text-primary-600" />
        </div>
        <h2 className="text-base font-bold text-content mb-2">{t('casesReports.comingSoon')}</h2>
        <p className="text-sm text-[var(--text-secondary)] max-w-md">{t('casesReports.comingSoonDescPhase' + phase)}</p>
      </div>
    </div>
  );
}

// ─── Phase 4: ExportButtons ───────────────────────────────────
function ExportButtons({ tab, von, bis, includeTest, t }) {
  const [busy, setBusy] = useState(null);
  const exportFile = async (fmt) => {
    setBusy(fmt);
    try {
      const res = await fetch(`${API}/api/cases/reports/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tab, format: fmt, von, bis, include_test: includeTest }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cases-${tab}-${von}-${bis}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { /* silent */ }
    setBusy(null);
  };
  const Btn = ({ fmt, Icon, label }) => (
    <button
      onClick={() => exportFile(fmt)}
      disabled={busy === fmt}
      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold text-content bg-surface-card border border-line hover:bg-surface-active disabled:opacity-60"
      title={label}
    >
      <Icon className="w-3.5 h-3.5" />
      {busy === fmt ? '…' : label}
    </button>
  );
  return (
    <div className="flex items-center gap-2">
      <Btn fmt="pdf" Icon={FileType} label={t('casesReports.exportPdf')} />
      <Btn fmt="xlsx" Icon={FileSpreadsheet} label={t('casesReports.exportXlsx')} />
      <Btn fmt="csv" Icon={FileText} label={t('casesReports.exportCsv')} />
    </div>
  );
}

// ─── Phase 3: TabCEO ──────────────────────────────────────────
function TabCEO({ data, t }) {
  if (!data) return <div className="bg-surface-card border border-line rounded-card p-12 text-center text-sm text-[var(--text-secondary)]">{t('casesReports.loading')}</div>;
  const k = data.kpi || {};
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label={t('casesReports.ceoKpiCases')} value={fmt(k.total_cases)} />
        <KpiCard label={t('casesReports.ceoKpiClosed')} value={fmt(k.closed_cases)} />
        <KpiCard label={t('casesReports.ceoKpiSlaPct')} value={k.sla_met_pct == null ? '—' : `${fmtD1(k.sla_met_pct)}%`} />
        <KpiCard label={t('casesReports.ceoKpiAvgRes')} value={k.avg_resolution_h == null ? '—' : `${fmtD1(k.avg_resolution_h)} h`} />
        <KpiCard label={t('casesReports.ceoKpiActiveCust')} value={fmt(k.active_customers)} />
        <KpiCard label={t('casesReports.ceoKpiCsat')} value={k.csat_avg == null ? '—' : `${fmtD1(k.csat_avg)} / 5`} />
        <KpiCard label={t('casesReports.ceoKpiCsatTotal')} value={fmt(k.csat_total)} />
      </div>

      <Section title={t('casesReports.ceoQuarterlyTitle')} subtitle={t('casesReports.ceoQuarterlySub')}>
        <div className="h-72 w-full">
          {!data.quarterly?.length ? <EmptyState t={t} /> : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.quarterly}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.line} />
                <XAxis dataKey="quarter" stroke={COLORS.grayText} fontSize={11} />
                <YAxis stroke={COLORS.grayText} fontSize={11} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="total" fill={COLORS.blue} name={t('casesReports.ceoColTotal')} />
                <Bar dataKey="closed" fill={COLORS.cyan} name={t('casesReports.ceoColClosed')} />
                <Bar dataKey="sla_met" fill={COLORS.emerald} name={t('casesReports.ceoColSlaMet')} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title={t('casesReports.ceoMonthlyTitle')} subtitle={t('casesReports.ceoMonthlySub')}>
          <div className="h-64 w-full">
            {!data.monthly?.length ? <EmptyState t={t} /> : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.line} />
                  <XAxis dataKey="monat" stroke={COLORS.grayText} fontSize={11} />
                  <YAxis stroke={COLORS.grayText} fontSize={11} />
                  <Tooltip content={<ChartTooltip />} />
                  <Area type="monotone" dataKey="total" stroke={COLORS.blue} fill={COLORS.blue + '33'} name={t('casesReports.ceoColTotal')} />
                  <Area type="monotone" dataKey="closed" stroke={COLORS.emerald} fill={COLORS.emerald + '33'} name={t('casesReports.ceoColClosed')} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </Section>

        <Section title={t('casesReports.ceoTeamTitle')} subtitle={t('casesReports.ceoTeamSub')}>
          <div className="h-64 w-full">
            {!data.team_workload?.length ? <EmptyState t={t} /> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.team_workload} dataKey="cases_total" nameKey="rolle" outerRadius={90} label={(e) => e.rolle}>
                    {data.team_workload.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Section>
      </div>

      <Section title={t('casesReports.ceoRiskTitle')} subtitle={t('casesReports.ceoRiskSub')}>
        {!data.risk_customers?.length ? <EmptyState t={t} /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-line">
                <th className="text-left py-2 px-3 text-xs font-semibold text-[var(--text-secondary)]">{t('casesReports.ceoColCustomer')}</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-[var(--text-secondary)]">{t('casesReports.ceoColTotal')}</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-[var(--text-secondary)]">{t('casesReports.ceoColClosed')}</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-[var(--text-secondary)]">{t('casesReports.ceoColSlaPct')}</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-[var(--text-secondary)]">{t('casesReports.ceoColAvgRes')}</th>
              </tr></thead>
              <tbody>
                {data.risk_customers.map(r => (
                  <tr key={r.kunde_id} className="border-b border-line/50 hover:bg-surface-active">
                    <td className="py-2 px-3 text-content">{r.kunde_name}</td>
                    <td className="py-2 px-3 text-right text-content">{fmt(r.cases_total)}</td>
                    <td className="py-2 px-3 text-right text-content">{fmt(r.cases_closed)}</td>
                    <td className={`py-2 px-3 text-right font-semibold ${r.sla_met_pct < 80 ? 'text-red-500' : r.sla_met_pct < 95 ? 'text-amber-500' : 'text-emerald-500'}`}>{r.sla_met_pct == null ? '—' : `${fmtD1(r.sla_met_pct)}%`}</td>
                    <td className="py-2 px-3 text-right text-content">{r.avg_resolution_h == null ? '—' : `${fmtD1(r.avg_resolution_h)} h`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

// ─── Phase 3: TabSchedule ─────────────────────────────────────
function TabSchedule({ t }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/cases/reports/schedules`, { credentials: 'include' });
      if (res.ok) {
        const json = await res.json();
        setSchedules(json.schedules || []);
      }
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (s) => {
    await fetch(`${API}/api/cases/reports/schedules/${s.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ active: !s.active }),
    });
    load();
  };

  const runNow = async (s) => {
    await fetch(`${API}/api/cases/reports/schedules/${s.id}/run-now`, { method: 'POST', credentials: 'include' });
    setTimeout(load, 1500);
  };

  const remove = async (s) => {
    if (!window.confirm(t('casesReports.scheduleConfirmDelete') + ` "${s.name}"?`)) return;
    await fetch(`${API}/api/cases/reports/schedules/${s.id}`, { method: 'DELETE', credentials: 'include' });
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--text-secondary)]">{t('casesReports.scheduleSub')}</p>
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-primary-500 text-white hover:bg-primary-600">
          <Plus className="w-3.5 h-3.5" /> {t('casesReports.scheduleAdd')}
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin h-6 w-6 border-2 border-primary-500 border-t-transparent rounded-full" /></div>
      ) : schedules.length === 0 ? (
        <div className="bg-surface-card border border-line rounded-card p-12 text-center text-sm text-[var(--text-secondary)]">{t('casesReports.scheduleEmpty')}</div>
      ) : (
        <div className="bg-surface-card border border-line rounded-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-line bg-[var(--bg-inset,#F8F9FC)]">
              <th className="text-left py-2.5 px-3 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('casesReports.scheduleColName')}</th>
              <th className="text-left py-2.5 px-3 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('casesReports.scheduleColTab')}</th>
              <th className="text-left py-2.5 px-3 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('casesReports.scheduleColFreq')}</th>
              <th className="text-left py-2.5 px-3 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('casesReports.scheduleColRecipients')}</th>
              <th className="text-left py-2.5 px-3 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('casesReports.scheduleColFormats')}</th>
              <th className="text-left py-2.5 px-3 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('casesReports.scheduleColNext')}</th>
              <th className="text-left py-2.5 px-3 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('casesReports.scheduleColLast')}</th>
              <th className="text-right py-2.5 px-3 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('casesReports.scheduleColActions')}</th>
            </tr></thead>
            <tbody>
              {schedules.map(s => (
                <tr key={s.id} className="border-b border-line/50 hover:bg-surface-active">
                  <td className="py-2.5 px-3 text-content font-medium">{s.name}</td>
                  <td className="py-2.5 px-3 text-content">{t(`casesReports.tab${s.tab_key.charAt(0).toUpperCase() + s.tab_key.slice(1)}`)}</td>
                  <td className="py-2.5 px-3 text-content">{t(`casesReports.freq${s.frequency.charAt(0).toUpperCase() + s.frequency.slice(1)}`)}</td>
                  <td className="py-2.5 px-3 text-[var(--text-secondary)] text-xs">{(s.recipients || []).join(', ')}</td>
                  <td className="py-2.5 px-3 text-[var(--text-secondary)] text-xs uppercase">{(s.formats || []).join(', ')}</td>
                  <td className="py-2.5 px-3 text-[var(--text-secondary)] text-xs">{s.next_run_at ? new Date(s.next_run_at).toLocaleString('de-DE') : '—'}</td>
                  <td className="py-2.5 px-3 text-[var(--text-secondary)] text-xs">{s.last_run_at ? `${new Date(s.last_run_at).toLocaleDateString('de-DE')} (${s.last_run_status || '—'})` : '—'}</td>
                  <td className="py-2.5 px-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => runNow(s)} title={t('casesReports.scheduleRunNow')} className="p-1.5 rounded hover:bg-surface-active text-emerald-500"><Play className="w-3.5 h-3.5" /></button>
                      <button onClick={() => toggleActive(s)} title={s.active ? t('casesReports.schedulePause') : t('casesReports.scheduleActivate')} className="p-1.5 rounded hover:bg-surface-active text-amber-500">{s.active ? <PauseIcon className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}</button>
                      <button onClick={() => remove(s)} title={t('casesReports.scheduleDelete')} className="p-1.5 rounded hover:bg-surface-active text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && <ScheduleAddModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} t={t} />}
    </div>
  );
}

function ScheduleAddModal({ onClose, onSaved, t }) {
  const [name, setName] = useState('');
  const [tabKey, setTabKey] = useState('overview');
  const [frequency, setFrequency] = useState('weekly');
  const [recipients, setRecipients] = useState('');
  const [formats, setFormats] = useState({ pdf: true, xlsx: false, csv: false });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setErr('');
    if (!name.trim()) { setErr(t('casesReports.scheduleErrName')); return; }
    const recpts = recipients.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);
    if (recpts.length === 0) { setErr(t('casesReports.scheduleErrRecipients')); return; }
    const fmts = Object.entries(formats).filter(([,v]) => v).map(([k]) => k);
    if (fmts.length === 0) { setErr(t('casesReports.scheduleErrFormat')); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/cases/reports/schedules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: name.trim(), tab_key: tabKey, frequency, recipients: recpts, formats: fmts, active: true }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); setErr(j.error || String(res.status)); setSaving(false); return; }
      onSaved();
    } catch (e) { setErr(String(e.message)); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface-card border border-line rounded-card max-w-md w-full p-5" onClick={e => e.stopPropagation()}>
        <h3 className="text-base font-bold text-content mb-3">{t('casesReports.scheduleAdd')}</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-[var(--text-secondary)] mb-1 block">{t('casesReports.scheduleColName')}</label>
            <input value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-1.5 rounded-md border border-line bg-surface-card text-sm text-content" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-[var(--text-secondary)] mb-1 block">{t('casesReports.scheduleColTab')}</label>
              <select value={tabKey} onChange={e => setTabKey(e.target.value)} className="w-full px-3 py-1.5 rounded-md border border-line bg-surface-card text-sm text-content">
                <option value="overview">{t('casesReports.tabOverview')}</option>
                <option value="operational">{t('casesReports.tabOperational')}</option>
                <option value="sla">{t('casesReports.tabSla')}</option>
                <option value="agent">{t('casesReports.tabAgent')}</option>
                <option value="customer">{t('casesReports.tabCustomer')}</option>
                <option value="ceo">{t('casesReports.tabCeo')}</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[var(--text-secondary)] mb-1 block">{t('casesReports.scheduleColFreq')}</label>
              <select value={frequency} onChange={e => setFrequency(e.target.value)} className="w-full px-3 py-1.5 rounded-md border border-line bg-surface-card text-sm text-content">
                <option value="daily">{t('casesReports.freqDaily')}</option>
                <option value="weekly">{t('casesReports.freqWeekly')}</option>
                <option value="monthly">{t('casesReports.freqMonthly')}</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[var(--text-secondary)] mb-1 block">{t('casesReports.scheduleColRecipients')}</label>
            <textarea value={recipients} onChange={e => setRecipients(e.target.value)} rows={2} placeholder="email1@x.de, email2@x.de" className="w-full px-3 py-1.5 rounded-md border border-line bg-surface-card text-sm text-content" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[var(--text-secondary)] mb-1 block">{t('casesReports.scheduleColFormats')}</label>
            <div className="flex gap-3">
              {['pdf','xlsx','csv'].map(f => (
                <label key={f} className="flex items-center gap-1.5 text-xs text-content">
                  <input type="checkbox" checked={!!formats[f]} onChange={e => setFormats(s => ({ ...s, [f]: e.target.checked }))} />
                  {f.toUpperCase()}
                </label>
              ))}
            </div>
          </div>
          {err && <div className="text-xs text-red-500">{err}</div>}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 rounded-md text-xs font-semibold bg-surface-active text-content">{t('casesReports.cancel')}</button>
          <button onClick={submit} disabled={saving} className="px-3 py-1.5 rounded-md text-xs font-semibold bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-60">{saving ? '…' : t('casesReports.save')}</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function CasesReports() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useTabParam('overview');

  // Default 30 Tage (#1061)
  const initRange = getDateRange('monat');
  const [von, setVon] = useState(initRange.von);
  const [bis, setBis] = useState(initRange.bis);
  const [preset, setPreset] = useState('monat');

  const [tabData, setTabData] = useState({});
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [includeTest, setIncludeTest] = useState(false); // #1245 Test-Cases ein-/ausblenden

  const ENDPOINT_MAP = useMemo(() => ({
    overview: '/api/cases/reports/overview',
    operational: '/api/cases/reports/operational',
    sla: '/api/cases/reports/sla',
    agent: '/api/cases/reports/agent',
    customer: '/api/cases/reports/customer-health',
    ceo: '/api/cases/reports/ceo',
  }), []);

  const testQs = includeTest ? '&include_test=1' : '';

  // Fetch /overview always (Top-KPIs + Übersicht-Tab)
  const fetchOverview = useCallback(async (v, b) => {
    try {
      const res = await fetch(`${API}/api/cases/reports/overview?von=${v}&bis=${b}${testQs}`, { credentials: 'include' });
      if (res.ok) setOverviewData(await res.json());
    } catch { /* silent */ }
  }, [testQs]);

  const fetchTab = useCallback(async (tab, v, b) => {
    if (!ENDPOINT_MAP[tab]) return; // Phase-2/3 stubs
    if (tab === 'overview') {
      // bereits via fetchOverview gefüllt
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}${ENDPOINT_MAP[tab]}?von=${v}&bis=${b}${testQs}`, { credentials: 'include' });
      if (res.ok) {
        const json = await res.json();
        setTabData(prev => ({ ...prev, [tab]: json }));
      }
    } catch { /* silent */ }
    setLoading(false);
  }, [ENDPOINT_MAP, testQs]);

  useEffect(() => { fetchOverview(von, bis); }, [von, bis, fetchOverview]);
  useEffect(() => { fetchTab(activeTab, von, bis); }, [activeTab, von, bis, fetchTab]);

  // #1245 Toggle ändert → Caches leeren, damit Tabs neu laden
  useEffect(() => { setTabData({}); setOverviewData(null); }, [includeTest]);

  const handleDateChange = ({ von: v, bis: b, preset: p }) => {
    setVon(v); setBis(b); setPreset(p);
    setTabData({});
    setOverviewData(null);
  };

  const TABS = TAB_DEFS.map(td => ({ ...td, label: t(td.labelKey) }));
  const currentData = tabData[activeTab];

  return (
    <div className="px-6 lg:px-8 py-6">
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold text-content">{t('casesReports.title')}</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">{t('casesReports.subtitleDashboard')}</p>
        </div>
        {activeTab !== 'schedule' && (
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs font-semibold text-[var(--text-secondary)] cursor-pointer select-none" title={t('casesReports.includeTestHint')}>
              <input
                type="checkbox"
                checked={includeTest}
                onChange={(e) => setIncludeTest(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-line accent-primary-500"
              />
              {t('casesReports.includeTest')}
            </label>
            <ExportButtons tab={activeTab} von={von} bis={bis} includeTest={includeTest} t={t} />
          </div>
        )}
      </div>

      <DateRangeFilter
        von={von}
        bis={bis}
        preset={preset}
        onChange={handleDateChange}
        presets={['monat', 'letzter_monat', '3m', '6m', '12m', 'gesamt']}
        className="mb-5"
      />

      {/* TOP-KPI-ROW (immer sichtbar) */}
      <TopKpis data={overviewData} t={t} />

      {/* Tab-Bar */}
      <div className="flex gap-1 mb-5 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-line overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-xs font-semibold transition-colors whitespace-nowrap ${
              activeTab === tab.key
                ? 'bg-white dark:bg-surface-elevated text-content shadow-sm'
                : 'text-[var(--text-secondary)] hover:text-content'
            }`}
          >
            <tab.Icon className="w-3.5 h-3.5" />
            {tab.label}
            {tab.phase > 1 && (
              <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500/10 text-amber-600 border border-amber-500/20">
                P{tab.phase}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading && !currentData && (
        <div className="flex justify-center py-20">
          <div className="animate-spin h-8 w-8 border-3 border-primary-500 border-t-transparent rounded-full" />
        </div>
      )}

      {(!loading || currentData) && (
        <>
          {activeTab === 'overview' && <TabUebersicht overviewData={overviewData} t={t} />}
          {activeTab === 'operational' && <TabOperational data={currentData} t={t} />}
          {activeTab === 'sla' && <TabSLA data={currentData} t={t} />}
          {activeTab === 'agent' && <TabAgent data={currentData} t={t} />}
          {activeTab === 'customer' && <TabCustomer data={currentData} von={von} bis={bis} includeTest={includeTest} t={t} />}
          {activeTab === 'ceo' && <TabCEO data={currentData} t={t} />}
          {activeTab === 'schedule' && <TabSchedule t={t} />}
        </>
      )}
    </div>
  );
}
